import React, { useState } from 'react';
import { UtensilsCrossed, Plus, X } from 'lucide-react';

const FoodPreferences = ({ preferences, onUpdate }) => {
  const [enabled, setEnabled] = useState(preferences?.enabled || false);
  const [selectedCuisines, setSelectedCuisines] = useState(preferences?.cuisines || []);
  const [selectedRestrictions, setSelectedRestrictions] = useState(preferences?.dietaryRestrictions || []);
  const [favoriteRestaurants, setFavoriteRestaurants] = useState(preferences?.favoriteRestaurants || []);
  const [cookingSkill, setCookingSkill] = useState(preferences?.cookingSkill || '');
  const [enjoysFineDining, setEnjoysFineDining] = useState(preferences?.enjoysFineDining || false);
  const [enjoysCasualDining, setEnjoysCasualDining] = useState(preferences?.enjoysCasualDining || true);
  const [foodie, setFoodie] = useState(preferences?.foodie || false);

  // New restaurant form
  const [newRestaurant, setNewRestaurant] = useState({ name: '', cuisine: '', location: '' });
  const [showRestaurantForm, setShowRestaurantForm] = useState(false);

  const cuisines = [
    'Italian', 'Chinese', 'Japanese', 'Thai', 'Indian', 'Mexican',
    'Greek', 'French', 'Vietnamese', 'Korean', 'Spanish', 'Lebanese',
    'American', 'Australian', 'British', 'German', 'Turkish',
    'Ethiopian', 'Caribbean', 'Moroccan', 'Fusion', 'Other'
  ];

  const dietaryRestrictions = [
    'None', 'Vegetarian', 'Vegan', 'Pescatarian', 'Halal', 'Kosher',
    'Gluten-Free', 'Dairy-Free', 'Nut Allergy', 'Keto', 'Paleo',
    'Low-Carb', 'Other'
  ];

  const cookingSkills = [
    'Beginner',
    'Intermediate',
    'Advanced',
    'Professional',
    'Prefer not to cook'
  ];

  const toggleCuisine = (cuisine) => {
    setSelectedCuisines(prev => 
      prev.includes(cuisine) 
        ? prev.filter(c => c !== cuisine)
        : [...prev, cuisine]
    );
  };

  const toggleRestriction = (restriction) => {
    setSelectedRestrictions(prev => 
      prev.includes(restriction) 
        ? prev.filter(r => r !== restriction)
        : [...prev, restriction]
    );
  };

  const addRestaurant = () => {
    if (newRestaurant.name.trim()) {
      setFavoriteRestaurants([...favoriteRestaurants, { ...newRestaurant }]);
      setNewRestaurant({ name: '', cuisine: '', location: '' });
      setShowRestaurantForm(false);
    }
  };

  const removeRestaurant = (index) => {
    setFavoriteRestaurants(favoriteRestaurants.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const foodData = {
      enabled,
      cuisines: selectedCuisines,
      dietaryRestrictions: selectedRestrictions,
      favoriteRestaurants,
      cookingSkill,
      enjoysFineDining,
      enjoysCasualDining,
      foodie
    };

    await onUpdate(foodData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <UtensilsCrossed className="w-6 h-6 text-orange-500" />
          <h3 className="text-xl font-semibold">Food & Dining Preferences</h3>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
        </label>
      </div>

      {enabled && (
        <div className="space-y-6">
          {/* Favorite Cuisines */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Favorite Cuisines
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:8}}>
              {cuisines.map(cuisine => (
                <button
                  key={cuisine}
                  onClick={() => toggleCuisine(cuisine)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    selectedCuisines.includes(cuisine)
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cuisine}
                </button>
              ))}
            </div>
          </div>

          {/* Dietary Restrictions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Dietary Restrictions
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:8}}>
              {dietaryRestrictions.map(restriction => (
                <button
                  key={restriction}
                  onClick={() => toggleRestriction(restriction)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    selectedRestrictions.includes(restriction)
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {restriction}
                </button>
              ))}
            </div>
          </div>

          {/* Cooking Skill */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Cooking Skill Level
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(90px, 1fr))', gap:8}}>
              {cookingSkills.map(skill => (
                <button
                  key={skill}
                  onClick={() => setCookingSkill(skill)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    cookingSkill === skill
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>

          {/* Favorite Restaurants */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Favorite Restaurants
              </label>
              <button
                onClick={() => setShowRestaurantForm(!showRestaurantForm)}
                className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Restaurant
              </button>
            </div>

            {showRestaurantForm && (
              <div className="bg-gray-50 p-4 rounded-lg mb-3 space-y-3">
                <input
                  type="text"
                  value={newRestaurant.name}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
                  placeholder="Restaurant name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={newRestaurant.cuisine}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, cuisine: e.target.value })}
                  placeholder="Cuisine type"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={newRestaurant.location}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, location: e.target.value })}
                  placeholder="Location"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addRestaurant}
                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowRestaurantForm(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {favoriteRestaurants.map((restaurant, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{restaurant.name}</p>
                    <p className="text-sm text-gray-600">
                      {restaurant.cuisine} {restaurant.location && `• ${restaurant.location}`}
                    </p>
                  </div>
                  <button
                    onClick={() => removeRestaurant(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Dining Preferences */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:16}}>
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={enjoysFineDining}
                onChange={(e) => setEnjoysFineDining(e.target.checked)}
                className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="text-gray-700">Enjoys fine dining</span>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={enjoysCasualDining}
                onChange={(e) => setEnjoysCasualDining(e.target.checked)}
                className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="text-gray-700">Enjoys casual dining</span>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={foodie}
                onChange={(e) => setFoodie(e.target.checked)}
                className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="text-gray-700">I'm a foodie</span>
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-medium"
            >
              Save Food Preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodPreferences;
